import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Calendar, ArrowLeft } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';
import { RichArticleContent } from '../components/RichArticleContent';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ArticleDetailPage = () => {
  const { slug } = useParams(); // Utilise le slug au lieu de l'ID
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
        
        // Charger les articles de la même catégorie
        if (response.data.category) {
          const relatedResponse = await axios.get(`${API}/articles?category=${response.data.category}`);
          const filtered = relatedResponse.data.filter(a => a.id !== response.data.id).slice(0, 3);
          setRelatedArticles(filtered);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement de l'article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-xl text-gray-600 mb-4">Article non trouvé</p>
            <Link to="/blog" className="text-honey hover:underline">
              ← Retour au blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <SEO 
        title={`${article.title} - UrbanHive`}
        description={article.excerpt}
        url={`/blog/${article.id}`}
        image={article.image}
        type="article"
      />
      <Header />
      
      <main>
        {/* Article Header */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Navigation et Badge */}
              <div className="flex items-center justify-between mb-6">
                <Link to="/blog" className="inline-flex items-center text-sage hover:text-honey transition-colors">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour au blog
                </Link>
                <Badge className="bg-honey text-white">{article.category}</Badge>
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-4">
                {article.title}
              </h1>
              
              <p className="text-xl text-gray-600 mb-6">
                {article.excerpt}
              </p>
              
              {/* Métadonnées */}
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>{new Date(article.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                <RichArticleContent content={article.content} />
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8">Articles similaires</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link 
                      key={related.id} 
                      to={`/blog/${related.slug || related.id}`}
                      className="group block bg-cream rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                    >
                      <div className="relative h-48">
                        <img 
                          src={related.image} 
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <Badge className="bg-honey text-white mb-2">{related.category}</Badge>
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-honey transition-colors">
                          {related.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {related.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};
